import { expected, schAloftArea, schSourceType } from '@aloft/toolkit-shared';

import { useEffect } from 'react';

import { resolveFeatureGridFieldConflicts } from '../../../../redux/features/actions/resolveFeatureGridFieldConflicts.ts';
import {
  selectFeatureGridActiveConflictedFields,
  selectFeatureGridConflictedFields,
} from '../../../../redux/features/selectors/uiSelectors.ts';
import { addFeatureGridConflictedField } from '../../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { PropertyGridFieldName } from '../../types.ts';
import { SourceOption } from './types.ts';

const GLA_FIELD_NAME = 'editableData.improvements.gla';
const GLA_CONFLICT_THRESHOLD = 0.05;

// GLA is a special case where we want to allow some variance between sources before flagging a conflict,
// due to differences in how GLA may be calculated or reported by different sources.
// This function checks if the variance between GLA values exceeds the defined threshold percentage.
// If we have more fields with similar special logic, we can consider abstracting this into a more generic function that takes a custom comparator.
export const glaOptionsConflict = (options: SourceOption[]): boolean => {
  const sqftValues = options
    .map((o) => schAloftArea.optional().parse(o.rawValue)?.toSqFt().unitAmount)
    .filter((v): v is number => v !== undefined);
  if (sqftValues.length < 2) return false;
  const min = Math.min(...sqftValues);
  const max = Math.max(...sqftValues);
  if (min === 0) return false;
  return (max - min) / min > GLA_CONFLICT_THRESHOLD;
};

type UseFieldConflictDetectionParams = {
  propertyDataFieldName: string;
  fieldName: PropertyGridFieldName;
  propertyId: string;
  fieldHasConflict: boolean;
  conflictIsResolved?: boolean;
  options: SourceOption[];
};

export const useFieldConflictDetection = ({
  propertyDataFieldName,
  fieldName,
  propertyId,
  fieldHasConflict,
  conflictIsResolved,
  options,
}: UseFieldConflictDetectionParams) => {
  const dispatch = useAppDispatch();
  const activeConflictedFields = useAppSelector(selectFeatureGridActiveConflictedFields);
  const allConflictedFields = useAppSelector(selectFeatureGridConflictedFields);

  useEffect(() => {
    if (conflictIsResolved) {
      const fieldIsInAllConflicts = propertyDataFieldName in allConflictedFields;
      const fieldIsInActiveConflicts = propertyDataFieldName in activeConflictedFields;

      // Seed the persistent set on initial load for previously-resolved conflicts.
      // Guard on fieldIsInAllConflicts to avoid dispatching on every render.
      if (!fieldIsInAllConflicts) {
        dispatch(
          addFeatureGridConflictedField({ [propertyDataFieldName]: { fieldName, propertyId } }),
        );
      }

      // Ensure the field is not in the active set.
      if (fieldIsInActiveConflicts) {
        const activeField = activeConflictedFields[propertyDataFieldName];
        if (activeField) {
          dispatch(
            resolveFeatureGridFieldConflicts({
              [propertyDataFieldName]: activeField,
            }),
          );
        }
      }
      return;
    }

    if (options.length === 0 || fieldHasConflict) return;

    const filteredOptions = options.filter(
      (option) =>
        option.value !== undefined &&
        `${option.value}` !== '0' &&
        option.source !== schSourceType.enum.unknown,
    );
    if (filteredOptions.length === 0) return;
    const hasConflict =
      fieldName === GLA_FIELD_NAME
        ? glaOptionsConflict(filteredOptions)
        : filteredOptions.some((o) => o.value !== expected(filteredOptions[0]).value);

    if (hasConflict) {
      dispatch(
        addFeatureGridConflictedField({ [propertyDataFieldName]: { fieldName, propertyId } }),
      );
    }
  }, [fieldHasConflict, conflictIsResolved, options, allConflictedFields, activeConflictedFields]);
};
